import styled, { keyframes } from "styled-components";

/* ─── Overlay & Container ─── */

export const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  /* small breathing room so the modal never touches the screen edges on phone
     (matches the other modals) */
  padding: 0 14px;
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
`;

export const ModalContainer = styled.div<{ $open?: boolean }>`
  position: relative;
  /* Static snapshot of the blurred glass look (2026-05-13). Replaces the
     live backdrop-filter so the modal looks identical on every page +
     scroll position. See docu/flows/auth/email-change.md "modal bg" note. */
  background:
    radial-gradient(ellipse 45% 40% at 42% -3%, rgba(176, 144, 105, 0.9) 0%, rgba(160, 128, 90, 0.5) 35%, transparent 75%),
    radial-gradient(ellipse 55% 50% at 58% 3%, rgba(170, 140, 100, 0.75) 0%, rgba(140, 110, 78, 0.35) 45%, transparent 85%),
    radial-gradient(ellipse 70% 35% at 50% 8%, rgba(150, 118, 82, 0.4) 0%, transparent 70%),
    linear-gradient(180deg, rgb(153, 112, 75) 0%, rgb(99, 74, 54) 50%, rgb(111, 83, 60) 100%);
  border: none;
  border-radius: 24px;
  box-shadow: ${({ $open }) => ($open ? "0 0 0 9999px rgba(0, 0, 0, 0.7), 0 20px 60px rgba(0, 0, 0, 0.6)" : "none")};
  width: 100%;
  max-width: 650px;
  /* Bumped to 95vh — gives steps with long line-item lists + tax breakdowns +
     buttons enough vertical room to render without internal scrolling on the
     common 800-1000px viewports. Body still falls back to overflow-y:auto for
     truly tall content (e.g. ridiculously many cart items). */
  max-height: 95vh;
  overflow: hidden;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: translateY(${({ $open }) => ($open ? "0" : "8px")});
  transition: opacity 0.25s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

export const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(235, 194, 142, 0.2);
  border: 1px solid rgba(235, 194, 142, 0.3);
  /* Dim by default → fully white on hover. */
  color: rgba(255, 255, 255, 0.55);
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(235, 194, 142, 0.35);
    border-color: rgba(235, 194, 142, 0.5);
    color: #ffffff;
  }
`;

/* ─── Slider ─── */

export const SliderViewport = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  /* overflow-x: clip has spotty cross-browser support and was leaving the
     slider's off-screen panels visually leaking through on some builds
     (panel 1 showing instead of panel 0 even with track translate=0).
     Use overflow: hidden — universally clips both axes. */
  overflow-x: hidden;
  transition: height 0.4s ease;
`;

export const SliderTrack = styled.div<{ $step: number; $panels: number }>`
  display: flex;
  align-items: stretch;
  width: ${({ $panels }) => $panels * 100}%;
  height: 100%;
  transform: translateX(${({ $step, $panels }) => -$step * (100 / $panels)}%);
  transition: transform 0.4s ease;
`;

export const SlidePanel = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
`;

export const SlidePanelBody = styled.div.attrs({ "data-slide-body": "" })`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* keep the cart's scroll from chaining into the page behind it */
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  /* Tighter symmetric left/right padding; the footer button uses the same
     insets so it lines up with the item width. The scrollbar gutter is
     reserved on the right so the bar always has its own space there. */
  padding: 24px 24px 18px;
  scrollbar-gutter: stable;
  /* Firefox: always-visible thin scrollbar */
  scrollbar-width: thin;
  scrollbar-color: rgba(235, 194, 142, 0.6) transparent;

  &::-webkit-scrollbar { width: 12px; }
  &::-webkit-scrollbar-track { background: transparent; }
  /* Always-visible thumb (only on this scroll area), brighter on hover */
  &::-webkit-scrollbar-thumb {
    background: rgba(235, 194, 142, 0.55);
    border-radius: 6px;
    border: 4px solid transparent;
    background-clip: padding-box;
    transition: background 0.15s ease;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(235, 194, 142, 0.75);
    background-clip: padding-box;
  }
`;

export const SlidePanelFooter = styled.div.attrs({ "data-slide-footer": "" })`
  flex-shrink: 0;
  /* Symmetric bottom inset = the 24px sides, so at REST the button has equal
     space on all sides; the button's hover lift then moves it up from there
     (previously the bottom only looked right while hovered). */
  padding: 12px 24px 24px;
`;

/* ─── Step Header ─── */

export const StepHeaderWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
`;

/* Cart-with-items header: a fixed (non-scrolling) top bar — left-aligned cart
   icon (no circle) + title. The close ✕ sits over its right side, so both live
   in a protected zone while the items scroll below. */
export const CartHeader = styled.div.attrs({ "data-slide-header": "" })`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  /* DESKTOP — bigger icon + title; sits lower than the ✕ (no need to centre on
     it) and close to the items below. */
  padding: 28px 24px 10px;

  & + [data-slide-body] {
    padding-top: 10px;
  }

  svg {
    width: 26px;
    height: 26px;
    /* Optically nudge the cart icon up 1px to sit level with the title (desktop). */
    position: relative;
    top: -1px;
  }

  h2 {
    font-family: "Cormorant Garamond", serif;
    font-size: 1.8rem;
    font-weight: 400;
    color: #ffffff;
    margin: 0;
    line-height: 1.2;
  }

  /* PHONE — smaller icon + title (still centred on the same ✕; 1.5rem line
     ≈29px → 19px top), and trim the cart-items body's top padding so the first
     item keeps a comfortable gap. */
  @media (max-width: 600px) {
    gap: 10px;
    padding: 19px 24px 16px;

    svg {
      width: 22px;
      height: 22px;
      top: 0; /* phone keeps the icon centred — no desktop nudge */
    }

    h2 {
      font-size: 1.5rem;
    }

    & + [data-slide-body] {
      padding-top: 16px;
    }
  }
`;

export const IconCircle = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid rgba(235, 194, 142, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;

  svg {
    width: 26px;
    height: 26px;
  }
`;

export const StepTitle = styled.h2`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.8rem;
  font-weight: 400;
  line-height: 1.2;
  color: #ffffff;
  margin: 0 0 6px;
  text-align: center;
`;

export const StepSubtitle = styled.p`
  font-family: "Raleway", sans-serif;
  font-size: 0.85rem;
  font-weight: 400;
  color: #e0d2c1;
  margin: 0;
  text-align: center;
`;

/* ─── Section Label (with side lines) ─── */

export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 28px;
  margin-bottom: 16px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(235, 194, 142, 0.2);
  }

  span {
    color: #fff;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    white-space: nowrap;
  }

  /* Phone — tighter section rhythm so taller steps (e.g. the plan
     configurator's price breakdown) sit higher / clear the fold. The first
     label has no big gap above it since the fixed header already spaces it. */
  @media (max-width: 600px) {
    margin-top: 18px;
    margin-bottom: 12px;

    &:first-child {
      margin-top: 4px;
    }
  }
`;

/* ─── Buttons ─── */

export const PrimaryBtn = styled.button<{ $dimmed?: boolean; $busy?: boolean }>`
  width: 100%;
  padding: 14px 28px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: ${({ $busy }) => ($busy ? "wait" : "pointer")};
  transition: transform 0.3s ease, box-shadow 0.3s ease, opacity 0.3s ease;
  border: none;
  background: linear-gradient(135deg, #ffd29b, #ffebb5);
  color: #3c2d23;
  box-shadow: 0 0 32px rgba(186, 144, 92, 0.4);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: ${({ $dimmed, $busy }) => ($dimmed || $busy ? 0.55 : 1)};

  /* Hover lift only on real pointers. On touch, iOS leaves :hover STUCK after a
     tap, so without this guard the button stays lifted (translateY(-3px)) and
     sits higher than the Back button beside it. */
  @media (hover: hover) {
    &:hover {
      transform: ${({ $dimmed, $busy }) => ($dimmed || $busy ? "none" : "translateY(-3px)")};
      box-shadow: ${({ $dimmed, $busy }) => ($dimmed || $busy ? "0 0 32px rgba(186, 144, 92, 0.4)" : "0 0 40px rgba(186, 144, 92, 0.6)")};
    }
  }
`;

const btnSpin = keyframes`
  to { transform: rotate(360deg); }
`;

export const BtnSpinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(60, 45, 35, 0.35);
  border-top-color: #3c2d23;
  border-radius: 50%;
  animation: ${btnSpin} 0.7s linear infinite;
  flex: 0 0 auto;
`;

export const SecondaryBtn = styled.button`
  width: 100%;
  padding: 12px 20px;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;
  border: 1px solid rgba(235, 194, 142, 0.4);
  color: #ebc28e;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: rgba(235, 194, 142, 0.1);
    border-color: #ebc28e;
  }
`;

export const BackBtn = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.08);
  border: none;
  color: #f7ede6;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    color: #9cd3fd;
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(235, 194, 142, 0.2);
`;

/* ─── Cart Item ─── */

export const CartItemRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  /* Glassy warm-gold panel — matches the recovery dialog / password
     flow glass treatment so the cart items read in the same visual
     language as the rest of the modals. */
  background: rgba(235, 194, 142, 0.14);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(235, 194, 142, 0.4);
  border-radius: 12px;
  margin-bottom: 14px;
  position: relative;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

/* thumbnail + info + delete row; the quantity stepper + price sit on their own
   row below — price pinned to the right edge of the item */
export const CartItemTop = styled.div`
  display: flex;
  gap: 14px;
`;

/* phone: qty stepper + price on their own row below the thumbnail */
export const CartItemBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (min-width: 601px) {
    display: none;
  }
`;

/* desktop: qty stepper + price on one line inside the info, right of the
   thumbnail and in line with the line-item amount */
export const CartItemInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;

  @media (max-width: 600px) {
    display: none;
  }
`;

/* Fixed 80px on phone; on desktop the box stretches to the row height so its
   bottom lines up with the qty switcher row (the image inside just fills it). */
export const CartThumbWrap = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;

  @media (min-width: 601px) {
    align-self: stretch;
    height: auto;
    width: 92px;
  }
`;

export const CartThumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const CartItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const CartItemTitle = styled.div`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.25rem;
  color: #ffffff;
  font-weight: 500;
  letter-spacing: 0.3px;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 32px;
`;

export const CartItemSub = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 0.8rem;
  color: #ffffff;
  margin-bottom: 12px;
`;

export const CartItemPrice = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 1rem;
  color: #ffffff;
  font-weight: 700;
`;

export const QuantityWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  padding: 4px 8px 4px 0;
  border-radius: 6px;
`;

export const QtyBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid rgba(235, 194, 142, 0.25);
  background: transparent;
  color: #ffffff;
  font-size: 1rem;
  font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
  line-height: 1;
  padding-bottom: 3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }

  /* phone: bigger tap target so fingers can press +/- reliably */
  @media (max-width: 600px) {
    width: 34px;
    height: 34px;
    font-size: 1.25rem;
  }
`;

export const QtyValue = styled.span`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.9rem;
  color: #f7ede6;
  min-width: 20px;
  text-align: center;
`;

export const DeleteBtn = styled.button`
  position: absolute;
  top: 18px;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
  }

  path {
    stroke: #b9a089;
    transition: stroke 0.2s;
  }

  /* Hover-tint only on real pointers. On touch (phone/tablet) iOS leaves
     :hover STUCK on the last-tapped element, so after removing an item the
     NEXT item's trash button would stay red/highlighted until you tap
     elsewhere. Gating behind (hover: hover) undoes that for touch. */
  @media (hover: hover) {
    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }

    &:hover path {
      stroke: #e47a6a;
    }
  }
`;

/* ─── Totals ─── */

export const TotalsWrap = styled.div`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(186, 144, 92, 0.3);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const VatNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  font-family: "Raleway", sans-serif;
  font-size: 0.75rem;
  color: #d7c6bc;
  margin-bottom: 4px;
`;

export const TotalRow = styled.div<{ $bold?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  /* Same font family across all rows (Subtotal / Tip / VAT / Total) — only
     size + weight + color differ to mark Total as the hierarchy peak. */
  font-family: "Raleway", sans-serif;
  font-size: ${({ $bold }) => ($bold ? "1.1rem" : "0.9375rem")};
  font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
  color: ${({ $bold }) => ($bold ? "#ffffff" : "#e0d2c1")};
`;

export const TotalAmount = styled.span<{ $bold?: boolean }>`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: ${({ $bold }) => ($bold ? "1.3rem" : "0.88rem")};
  color: #ffffff;
  /* Only the Total amount is bold — Subtotal / Tip / VAT stay regular weight. */
  font-weight: ${({ $bold }) => ($bold ? 700 : 400)};
`;

/* ─── Empty State ─── */

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 16px;

  p {
    font-family: "Raleway", sans-serif;
    font-size: 0.95rem;
    color: #b9a089;
    margin: 0;
  }
`;

/* ─── Checkbox ─── */

export const CheckboxRow = styled.div`
  display: flex;
  /* Align the box to the FIRST line of the label (its center lands on the first
     line, the box being ~one line-box tall), not the vertical middle of a
     multi-line label — so the check reads as belonging to the opening words. */
  align-items: flex-start;
  gap: 14px;
  cursor: pointer;
  padding: 16px 20px;
  background: rgba(235, 194, 142, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: none;
  border-radius: 8px;
  margin-bottom: 12px;
  transition: all 0.3s ease;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const CheckboxBox = styled.div<{ $checked: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 2px solid ${({ $checked }) => ($checked ? "#9cd3fd" : "rgba(235, 194, 142, 0.4)")};
  background: ${({ $checked }) => ($checked ? "#9cd3fd" : "transparent")};
  box-shadow: ${({ $checked }) => ($checked ? "0 0 20px rgba(156, 211, 253, 0.4)" : "none")};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  svg {
    width: 12px;
    height: 12px;
    opacity: ${({ $checked }) => ($checked ? 1 : 0)};
  }
`;

export const CheckboxLabel = styled.span`
  font-size: 0.9rem;
  color: #f7ede6;
  line-height: 1.6;

  a {
    color: #ebc28e;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

export const NoticeWithCheckbox = styled.div`
  padding: 20px;
  background: rgba(235, 194, 142, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: none;
  border-radius: 8px;
  margin-bottom: 12px;
`;

export const NoticeText = styled.div`
  color: #f7ede6;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(235, 194, 142, 0.2);

  p { margin: 0; }
`;

export const InlineCheckboxRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  cursor: default;
`;

/* ─── Form Inputs ─── */

export const InputGroup = styled.div`
  margin-bottom: 14px;
`;

export const InputLabel = styled.label`
  display: block;
  font-family: "Raleway", sans-serif;
  font-size: 0.78rem;
  font-weight: 500;
  color: #b9a089;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export const InputField = styled.input<{ $error?: boolean }>`
  width: 100%;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid ${({ $error }) => ($error ? "#e47a6a" : "rgba(255, 255, 255, 0.2)")};
  border-radius: 8px;
  color: #ffffff;
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;

  &::placeholder {
    color: #ffffff;
    opacity: 0.7;
  }

  &:focus {
    border-color: rgba(235, 194, 142, 0.5);
    background: rgba(255, 255, 255, 0.14);
    box-shadow: 0 0 12px rgba(186, 144, 92, 0.15);
  }
`;

export const SelectField = styled.select<{ $error?: boolean }>`
  width: 100%;
  padding: 14.5px 16px;
  background: rgba(235, 194, 142, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid ${({ $error }) => ($error ? "#e47a6a" : "rgba(235, 194, 142, 0.2)")};
  border-radius: 8px;
  color: #f7ede6;
  font-family: "Raleway", sans-serif;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23b9a089' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;

  &:focus {
    border-color: rgba(235, 194, 142, 0.5);
  }
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

export const ErrorText = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 0.75rem;
  color: #e47a6a;
  margin-top: 4px;
  display: block;
`;

/* ─── Tip Grid ─── */

export const TipGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`;

export const TipCard = styled.button<{ $active: boolean }>`
  padding: 20px 16px;
  border-radius: 12px;
  border: 2px solid ${({ $active }) => ($active ? "#9cd3fd" : "rgba(235, 194, 142, 0.15)")};
  min-height: 80px;
  background: ${({ $active }) => ($active ? "rgba(156, 211, 253, 0.12)" : "rgba(235, 194, 142, 0.06)")};
  box-shadow: ${({ $active }) => ($active ? "0 0 20px rgba(156, 211, 253, 0.2)" : "none")};
  cursor: pointer;
  text-align: center;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ $active }) => ($active ? "#9cd3fd" : "rgba(235, 194, 142, 0.4)")};
  }
`;

export const TipPercent = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 1.3rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 2px;
`;

export const TipLabel = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.85rem;
  color: #9cd3fd;
`;

export const TipCustomStepper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  /* Press-and-hold on touch must never select the +/− glyph or the amount, and
     iOS's tap-and-hold callout must not appear. (Inherits to the children.) */
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
`;

export const TipStepperBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(235, 194, 142, 0.3);
  background: transparent;
  color: #ebc28e;
  font-size: 1.4rem;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  /* Hover bg only on real pointers — on touch iOS leaves :hover stuck after a
     tap/hold, which lit the button up during a press-and-hold. */
  @media (hover: hover) {
    &:hover {
      background: rgba(235, 194, 142, 0.1);
    }
  }
`;

export const TipCustomValue = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 1.15rem;
  font-weight: 600;
  color: #9cd3fd;
  min-width: 60px;
  text-align: center;
`;

export const SkipRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin-top: 8px;
  padding: 20px 16px;
  border-radius: 8px;
  border: 2px solid rgba(235, 194, 142, 0.1);
  min-height: 80px;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:hover {
    border-color: rgba(235, 194, 142, 0.25);
  }

  span {
    font-family: "Raleway", sans-serif;
    font-size: 1rem;
    color: #fff;
  }
`;

/* ─── Review Items ─── */

export const ReviewItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  /* Match the cart item glass treatment for visual consistency across
     the checkout flow (CartView item rows → ReviewView line items). */
  background: rgba(235, 194, 142, 0.14);
  border: 1px solid rgba(235, 194, 142, 0.4);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 8px;
  margin-bottom: 8px;
`;

export const ReviewItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

export const ReviewThumb = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

export const ReviewItemName = styled.div`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.2rem;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ReviewItemQty = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.875rem;
  color: #ffffff;
  margin-top: -2px;
`;

export const ReviewItemPrice = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 1rem;
  color: #fff;
  flex-shrink: 0;
  margin-left: 12px;
`;

/* ─── Checkout view states ─── */

export const GuestNotice = styled.div`
  background: rgba(52, 211, 153, 0.1);
  border: 1px solid rgba(52, 211, 153, 0.25);
  border-top: 3px solid rgba(52, 211, 153, 0.5);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;

  p {
    font-family: "Raleway", sans-serif;
    font-size: 0.9rem;
    color: #f7ede6;
    line-height: 1.6;
    margin: 0;
  }
`;

/* ─── Review billing address ─── */

export const BillingBlock = styled.div`
  background: rgba(235, 194, 142, 0.06);
  border-radius: 8px;
  border: 1px solid rgba(235, 194, 142, 0.15);
  padding: 16px 20px;
  margin-bottom: 16px;

  p {
    font-family: "Raleway", sans-serif;
    font-size: 0.9rem;
    color: #f7ede6;
    line-height: 1.6;
    margin: 0;
  }
`;
